import React, { useState, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { InfoIcon } from 'lucide-react';

import { QUESTION_TYPE } from 'types/question.types';
import {
  FormField,
  FormFieldSet,
  FormItem,
  FormControl,
  MaxLengthInputField,
  SwitchField,
  TextareaField
} from '@epoch-ui/components/Form';
import { ComboBox } from '@epoch-ui/components/ComboBox/ComboBox';
import { Command, CommandList, CommandItem } from '@epoch-ui/components/Command/command';

import {
  LinearScaleFormValues,
  NPSFormValues,
  SatisfactionFormValues,
  ConnectionFormValues,
  RetentionFormValues
} from '../schemas/LinearScaleSchema';
import { getLinearScaleTypeInfo } from '../config/linearScaleTypeInfo';

type AnyLinearScaleFormValues =
  | LinearScaleFormValues
  | NPSFormValues
  | SatisfactionFormValues
  | ConnectionFormValues
  | RetentionFormValues;

interface LinearScaleFormProps {
  disabled?: boolean;
}

export const LinearScaleForm: React.FC<LinearScaleFormProps> = ({ disabled = false }) => {
  const { register, control } = useFormContext<AnyLinearScaleFormValues>();

  const type = useWatch({ control, name: 'type' });
  const minValue = useWatch({ control, name: 'properties.scaleMinValue' });
  const maxValue = useWatch({ control, name: 'properties.scaleMaxValue' });

  // Local states to open/close combos
  const [isMinOpen, setIsMinOpen] = useState(false);
  const [isMaxOpen, setIsMaxOpen] = useState(false);

  const { title, description } = useMemo(() => getLinearScaleTypeInfo(type), [type]);

  // Possible choices for minValue => 0 or 1
  const possibleMins = useMemo(() => [0, 1], []);

  // For maxValue => from (minValue+1)..10
  const possibleMaxes = useMemo(
    () => Array.from({ length: 10 - (minValue + 1) + 1 }, (_, i) => i + minValue + 1),
    [minValue]
  );

  // Only allow editing scale values and labels for LINEAR_SCALE type
  const isLinearScaleType = useMemo(() => type === QUESTION_TYPE.LINEAR_SCALE, [type]);
  const isFieldDisabled = useMemo(
    () => !isLinearScaleType || disabled,
    [isLinearScaleType, disabled]
  );

  return (
    <div className="tw-flex tw-flex-grow tw-flex-col">
      <div className="tw-mb-6">
        <div className="tw-mb-[2px] tw-text-xl tw-font-medium tw-leading-7 tw-text-black">
          {title}
        </div>
        <span className="tw-text-sm tw-font-normal tw-text-primary-grey">{description}</span>
      </div>

      <FormFieldSet className="tw-gap-0">
        {/* Question prompt */}
        <TextareaField
          {...register('label')}
          label="Question"
          disabled={disabled}
          className="tw-mb-6 tw-mt-0"
        />

        {/* Options */}
        <span className="tw-mb-3 tw-font-medium tw-text-dark-black">Options</span>
        <div className="border-left tw-pl-4">
          {/* Min/Max comboboxes */}
          <div className="tw-mb-4 tw-flex tw-w-[201px] tw-items-center tw-gap-4">
            <FormField
              name="properties.scaleMinValue"
              control={control}
              render={({ field }): JSX.Element => (
                <FormItem>
                  <FormControl>
                    <ComboBox
                      triggerContent={String(field.value)}
                      isOpen={isMinOpen}
                      onOpenChange={setIsMinOpen}
                      className="tw-text-dark-black"
                      disabled={isFieldDisabled}
                    >
                      <Command>
                        <CommandList>
                          {possibleMins.map((val) => (
                            <CommandItem
                              key={val}
                              onSelect={(): void => {
                                field.onChange(val);
                                setIsMinOpen(false);
                              }}
                            >
                              <div className="tw-text-dark-black">{String(val)}</div>
                            </CommandItem>
                          ))}
                        </CommandList>
                      </Command>
                    </ComboBox>
                  </FormControl>
                </FormItem>
              )}
            />

            <span className="tw-mt-2">to</span>

            <FormField
              name="properties.scaleMaxValue"
              control={control}
              render={({ field }): JSX.Element => (
                <FormItem>
                  <FormControl>
                    <ComboBox
                      triggerContent={String(field.value)}
                      isOpen={isMaxOpen}
                      onOpenChange={setIsMaxOpen}
                      className="tw-text-dark-black"
                      disabled={isFieldDisabled}
                    >
                      <Command>
                        <CommandList>
                          {possibleMaxes.map((val) => (
                            <CommandItem
                              key={val}
                              onSelect={(): void => {
                                field.onChange(val);
                                setIsMaxOpen(false);
                              }}
                            >
                              {String(val)}
                            </CommandItem>
                          ))}
                        </CommandList>
                      </Command>
                    </ComboBox>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* minLabel */}
          <div className="tw-mb-4">
            <MaxLengthInputField
              {...register('properties.scaleMinLabel')}
              label={
                <span className="tw-text-xs tw-font-normal tw-text-primary-grey">
                  Label for {minValue} {isLinearScaleType ? '(optional)' : ''}
                </span>
              }
              data-testid="min-label-input"
              disabled={isFieldDisabled}
              inputClassName="tw-w-full"
              maxLength={25}
            />
          </div>

          {/* maxLabel */}
          <div className="tw-mb-4">
            <MaxLengthInputField
              {...register('properties.scaleMaxLabel')}
              label={
                <span className="tw-text-xs tw-font-normal tw-text-primary-grey">
                  Label for {maxValue} {isLinearScaleType ? '(optional)' : ''}
                </span>
              }
              data-testid="max-label-input"
              disabled={isFieldDisabled}
              inputClassName="tw-w-full"
              maxLength={25}
            />
          </div>
          {!isLinearScaleType && (
            <div className="tw-text-xs tw-leading-4 tw-text-primary-grey">
              <InfoIcon className="tw-mr-1 tw-h-4 tw-w-4 tw-text-primary-grey" />
              The scale can&apos;t be changed for engagement metrics.{' '}
              <a
                href="https://help.epochapp.com/articles/what-are-engagement-metrics"
                target="_blank"
                rel="noreferrer"
                className="tw-text-xs tw-text-primary-grey tw-underline hover:tw-text-foreground"
              >
                Learn more
              </a>
            </div>
          )}
        </div>

        {/* Required toggle */}
        <SwitchField
          {...register('isRequired')}
          label="Required"
          disabled={disabled}
          className="tw-mt-4"
        />
      </FormFieldSet>
    </div>
  );
};
